/**
 * @ferrumec/auth — core type definitions.
 *
 * Nothing in this file knows about JWT, OAuth2, Keycloak, Auth0, or any
 * specific backend. Every provider implementation adapts to these shapes.
 */

/** The authenticated user. Deliberately minimal — apps extend via `[key: string]: unknown`. */
export interface User {
  id: string;
  email?: string;
  username?: string;
  displayName?: string;
  avatarUrl?: string;
  [key: string]: unknown;
}

/** Credentials accepted by password-based providers (e.g. JwtProvider). */
export interface PasswordCredentials {
  identifier: string; // email or username
  password: string;
  rememberMe?: boolean;
}

/** Credentials submitted to create a new account (username/password providers only). */
export interface RegisterCredentials {
  username: string;
  password: string;
}

/**
 * Returned when an email confirmation code is requested. `nonce` is opaque —
 * hold onto it (e.g. in component state) and pass it back unchanged to
 * `confirmEmailConfirmation` along with the code the user received.
 */
export interface EmailConfirmationHandle {
  nonce: string;
}

/** Marker type — redirect-flow providers (OAuth2/OIDC family) take no credentials at call time. */
export type RedirectCredentials = Record<string, never>;

export type Credentials = PasswordCredentials | RedirectCredentials;

/** The token pair persisted by a TokenStorage implementation. */
export interface Tokens {
  accessToken: string;
  refreshToken?: string;
  /** Epoch milliseconds when the access token expires. */
  expiresAt?: number;
}

/** Storage abstraction. No implementation may assume `window.localStorage` exists. */
export interface TokenStorage {
  load(): Tokens | null;
  save(tokens: Tokens): void;
  clear(): void;
}

/** Configurable endpoint map. The package never hardcodes a path. */
export interface AuthEndpoints {
  login?: string;
  logout?: string;
  refresh?: string;
  register?: string;
  currentUser?: string;
  forgotPassword?: string;
  resetPassword?: string;
  verifyEmail?: string;
  changePassword?: string;
  authorize?: string; // OAuth2/OIDC authorization endpoint
  token?: string; // OAuth2/OIDC token endpoint
  userInfo?: string; // OIDC userinfo endpoint
  endSession?: string; // OIDC RP-initiated logout
}

/** Lifecycle events every provider/session must be able to emit. */
export interface AuthEventMap {
  login: { user: User };
  logout: { reason?: "user" | "sessionExpired" | "idle" };
  refresh: { tokens: Tokens };
  sessionExpired: undefined;
  userChanged: { user: User | null };
}

export type AuthEventName = keyof AuthEventMap;

export type AuthEventListener<E extends AuthEventName> = (
  payload: AuthEventMap[E]
) => void;

export interface EventEmitter {
  on<E extends AuthEventName>(event: E, listener: AuthEventListener<E>): () => void;
  emit<E extends AuthEventName>(event: E, payload: AuthEventMap[E]): void;
}

/**
 * The single contract every authentication mechanism must implement.
 * The rest of the application (UI, routing, HTTP client) only ever
 * talks to this interface — never to a specific provider's internals.
 */
export interface AuthProvider {
  /** Restore any existing session (e.g. from storage) and validate it. */
  initialize(): Promise<void>;
  login(credentials: Credentials): Promise<void>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
  getAccessToken(): Promise<string | null>;
  getCurrentUser(): Promise<User | null>;
  isAuthenticated(): boolean;
  readonly events: EventEmitter;
}

/**
 * Optional extension of `AuthProvider` for backends that support self-service
 * account creation plus a "confirm your address with a code we emailed you"
 * step. Only username/password-style providers (e.g. `JwtProvider`)
 * implement this — redirect-flow providers (OAuth2/OIDC, Keycloak, Auth0,
 * Azure) don't, since the identity provider owns registration there.
 *
 * `AuthProvider` (the React component) feature-detects this interface at
 * runtime and exposes `register` / `requestEmailConfirmation` /
 * `confirmEmailConfirmation` on `useAuth()` only when the configured
 * provider implements it.
 */
export interface RegistrableAuthProvider extends AuthProvider {
  /** Create the account and, on success, start an authenticated session — mirrors `login()`. */
  register(credentials: RegisterCredentials): Promise<void>;
  /** Ask the backend to email a confirmation code to `email`. Requires an active session. */
  requestEmailConfirmation(email: string): Promise<EmailConfirmationHandle>;
  /** Redeem the code the user received against the `nonce` from `requestEmailConfirmation`. */
  confirmEmailConfirmation(nonce: string, code: string): Promise<void>;
}

/** Normalized error shape produced by the HTTP client for every failure. */
export class AuthHttpError extends Error {
  readonly status: number | null;
  readonly code: string;
  readonly body: unknown;

  constructor(message: string, opts: { status: number | null; code: string; body?: unknown }) {
    super(message);
    this.name = "AuthHttpError";
    this.status = opts.status;
    this.code = opts.code;
    this.body = opts.body;
  }
}

export interface RequestConfig extends Omit<RequestInit, "body"> {
  body?: unknown; // auto-JSON-serialized unless a BodyInit is passed directly
  timeoutMs?: number;
  /** Skip Authorization header injection (e.g. for the login call itself). */
  skipAuth?: boolean;
  /** Skip the automatic 401 -> refresh -> retry cycle. */
  skipAuthRetry?: boolean;
}
